import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';

import { checkDeployEnv } from 'utils/checkDeployEnv';

import { getProductOverlaidList } from 'services/overlay';

import * as overlayEndpoints from 'services/overlay/endpoints';
import * as productEndpoints from 'services/product/endpoints';

import * as overlayServices from 'services/overlay';

const productOverlaidList = {
  url: overlayEndpoints.GET_PRODUCT_OVERLAID_LIST,
  all: ['pim/overlays', 'get-product-overlaid-list'],
  one: (productId) => [
    ...productOverlaidList.all,
    { params: { originalProductId: productId } },
  ],
};
const productHeaderBackground = {
  url: productEndpoints.GET_PRODUCT_HEADER_BACKGROUND,
  all: ['pim', 'get-product-background-header'],
  one: (productId) => [
    ...productHeaderBackground.all,
    { params: { productId } },
  ],
};

const productCompanyInfo = {
  url: productEndpoints.GET_PRODUCT_COMPANY_INFO,
  all: ['pim', 'get-product-company-information'],
  one: (productId) => [...productCompanyInfo.all, { params: { productId } }],
};

export const keys = {
  productOverlaidList,
  productHeaderBackground,
  productCompanyInfo,
};

export const useInvalidProductHeaderBackground = () => {
  const queryClient = useQueryClient();

  const invalidateProductHeaderBackgroundQueries = () => {
    queryClient.invalidateQueries({
      queryKey: keys.productHeaderBackground.all,
    });
  };

  return { invalidateProductHeaderBackgroundQueries };
};

export const useProductOverlaidList = (params, options = {}) => {
  const { productId } = params || {};
  const queryClient = useQueryClient();

  const isProductionEnv = checkDeployEnv('production');
  const isDemoEnv = checkDeployEnv('demo');
  const isAvailableEnv = !isProductionEnv && !isDemoEnv;

  const queryObject = useQuery({
    queryKey: keys.productOverlaidList.one(productId),
    queryFn: async () => {
      const params = {
        pageSize: 20,
        pageIndex: 1,
        sort: [
          {
            fieldName: 'id',
            isAscending: false,
          },
        ],
        filters: [],
        search: {
          searchText: '',
        },
        originalProductId: productId,
      };
      const { isSuccess, data, message } = await getProductOverlaidList(params);

      if (isSuccess) {
        return Promise.resolve(data);
      } else {
        return Promise.reject(message || 'Server Error');
      }
    },
    enabled: Boolean(productId) && isAvailableEnv,
    ...options,
  });

  const invalidateProductOverlaidList = (originProductId) => {
    const queryKey = !!originProductId
      ? keys.productOverlaidList.one(originProductId)
      : keys.productOverlaidList.all;

    queryClient.invalidateQueries({
      queryKey,
    });
  };
  return { invalidateProductOverlaidList, ...queryObject };
};

/**
 ** delete product overlay - used for product overlay at:
 ** 1. product full view ribbon
 ** 2. product grid
 ** 3. product overlay grid
 * @returns
 */
export const useMutationDeleteProductOverlays = () => {
  return useMutation({
    mutationFn: overlayServices.deleteProductOverlays,
  });
};

/**
 ** re-target product overlay - used for product overlay at:
 ** 1. product full view ribbon
 ** 2. product grid
 ** 3. product overlay grid
 * @returns
 */
export const useMutationRetargetOverlay = () => {
  return useMutation({
    mutationFn: overlayServices.retargetProductOverlay,
  });
};

export const useQueryProductCompanyInfo = ({ productId, ...options } = {}) => {
  const queryClient = useQueryClient();

  const queryObject = useQuery({
    queryKey: keys.productCompanyInfo.one(productId),
    enabled: Boolean(productId),
    retry: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  const invalidateProductCompanyInfo = (productId) => {
    queryClient.invalidateQueries({
      queryKey: productId
        ? keys.productCompanyInfo.one(productId)
        : keys.productCompanyInfo.all,
    });
  };

  return {
    ...queryObject,
    invalidateProductCompanyInfo,
  };
};
